import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  MapPin, 
  Package, 
  Award, 
  CheckCircle2,
  ArrowUpRight,
  Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
        
        const relatedRes = await axios.get(`${API}/products`, {
          params: { category: response.data.category_id }
        });
        setRelatedProducts(
          relatedRes.data.filter(p => p.slug !== slug).slice(0, 3)
        );
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9f7] pt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted animate-pulse" />
            <div className="space-y-6">
              <div className="h-6 bg-muted w-24 animate-pulse" />
              <div className="h-12 bg-muted w-3/4 animate-pulse" />
              <div className="h-24 bg-muted animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#f9f9f7] pt-24" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary-900 transition-colors group"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              {t('common.back')}
            </button>
            <button className="p-2 hover:bg-muted transition-colors">
              <Share2 className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="aspect-square overflow-hidden bg-white border border-border sticky top-28">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Category */}
            <Link
              to={`/products?category=${product.category_id}`}
              className="inline-block text-xs font-mono uppercase tracking-[0.2em] text-secondary-600 hover:text-secondary-500 transition-colors"
            >
              {product.category_name}
            </Link>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-900 tracking-tight" data-testid="product-title">
              {product.name}
            </h1>

            {/* Description */}
            <p className="text-lg text-muted-foreground leading-relaxed">
              {product.description}
            </p>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-3 gap-6 py-8 border-y border-border">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t('products.min_order')}</span>
                </div>
                <p className="font-heading font-bold text-primary-900">{product.min_order}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{t('products.origin')}</span>
                </div>
                <p className="font-heading font-bold text-primary-900">{product.origin}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-secondary-500" />
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Certified</span>
                </div>
                <p className="font-heading font-bold text-primary-900">{product.certifications.length}</p>
              </div>
            </div>

            {/* Specifications */}
            <div>
              <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-6">
                {t('products.specifications')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3 p-4 bg-white border border-border">
                    <CheckCircle2 className="w-4 h-4 text-secondary-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground capitalize mb-1">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="font-mono font-medium text-primary-900">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">
                {t('products.certifications')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.certifications.map((cert, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-primary-900 text-white text-xs font-mono uppercase tracking-wider"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to={`/rfq?product=${product.name}&category=${product.category_id}`} className="flex-1 min-w-[200px]">
                <Button 
                  className="w-full bg-primary-900 hover:bg-primary-800 text-white rounded-none py-7 text-xs font-bold uppercase tracking-[0.15em] group"
                  data-testid="request-quote-btn"
                >
                  {t('products.request_quote')}
                  <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="bg-transparent border-2 border-primary-900 text-primary-900 hover:bg-primary-900 hover:text-white rounded-none py-7 px-8 text-xs font-bold uppercase tracking-[0.15em]"
                  data-testid="contact-btn"
                >
                  {t('nav.contact')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24 md:mt-32 pt-16 border-t border-border">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-500 mb-3">
                  More From This Category
                </p>
                <h2 className="text-3xl font-heading font-bold text-primary-900">
                  Related Products
                </h2>
              </div>
              <Link to={`/products?category=${product.category_id}`}>
                <Button variant="outline" className="rounded-none border-2 border-primary-900 text-primary-900 hover:bg-primary-900 hover:text-white px-6 py-3 text-xs font-bold uppercase tracking-widest">
                  View All
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relProduct, index) => (
                <motion.div
                  key={relProduct.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProductCard product={relProduct} />
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
